<template>
    <div class="checkpoint-inspection-navigator">
        <div class="checkpoint-inspection-navigator-buttons">
            <div class="back-button-purple">
                <StandardButton @click="showChecklistInspectionTab" type="magenta" variant="primary" size="small" label="Back to Checkpoints" leftIcon="chevronLeftDoubleBack" />
            </div>
            <StandardButton v-if="currentCheckpoint > 0" type="magenta" variant="outline" size="small" label="Previous" leftIcon="chevronLeftBack" @click="$emit('previous')" />
            <div>steps {{ currentCheckpoint + 1 }} out of {{ totalCheckpoint }}</div>
        </div>
        <div class="checkpoint-inspection-navigator-inspection-action">
            <InspectionAction
                :view="inspectionView"
                :isButtonTypeLarge="true"
                :isPassed="isPassed"
                :isFailed="isFailed"
                :isNotApplicable="isNotApplicable"
                :isObsolete="isObsolete"
                :avatar="evaluationItem?.inspected_by_avatar || null"
                :updated_on="evaluationItem?.inspected_on || null"
                :failed_reason="evaluationItem?.reason || ''"
                :not_applicable_reason="evaluationItem?.reason || ''"
                :obsolete_reason="evaluationItem?.reason || ''"
                :updated_by="{
                    name: evaluationItem?.inspected_by || null,
                    picture: evaluationItem?.inspected_by_avatar || null,
                }"
                @onPass="emit('onPass')"
                @onFail="(reason) => emit('onFail', reason)"
                @onNotApplicable="(reason) => emit('onNotApplicable', reason)"
                @onClose="emit('onClose')"
            />
            <StandardButton v-if="currentCheckpoint < totalCheckpoint - 1" type="magenta" variant="outline" size="small" label="Next" rightIcon="chevronRightForward" @click="$emit('next')" />
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import StandardButton from '@components/StandardButton/StandardButton.vue';
import InspectionAction from '@components/InspectionAction/InspectionAction.vue';

const props = defineProps({
    checkpoints: { type: Array, required: true },
    currentCheckpoint: { type: Number, required: true },
    totalCheckpoint: { type: Number, required: true },
    evaluationItem: { type: Object, default: null },
    hasFilter: { type: Boolean, default: false },
});

const emit = defineEmits(['showChecklistInspectionTab', 'next', 'previous', 'onPass', 'onFail', 'onNotApplicable', 'onClose']);

const STATUS_VIEW_MAP = {
    passed: 'passed-evaluation',
    failed: 'failed-evaluation',
    pending: 'pending-evaluation',
    not_applicable: 'notapplicable-evaluation',
    notapplicable: 'notapplicable-evaluation',
    'n/a': 'notapplicable-evaluation',
    reset: 'reset-pending-evaluation',
    obsolete: 'obsolete-evaluation',
};

const normalizedStatus = computed(() =>
    String(props.evaluationItem?.status ?? 'pending').toLowerCase().replace(/[\s_]+/g, '_').replace(/\//g, '/')
);

const inspectionView = computed(() => STATUS_VIEW_MAP[normalizedStatus.value] ?? 'pending-evaluation');
const isPassed = computed(() => normalizedStatus.value === 'passed');
const isFailed = computed(() => normalizedStatus.value === 'failed');
const isNotApplicable = computed(() => ['not_applicable', 'notapplicable', 'n/a'].includes(normalizedStatus.value));
const isObsolete = computed(() => normalizedStatus.value === 'obsolete');

const showChecklistInspectionTab = () => emit('showChecklistInspectionTab');
</script>

<style scoped>
.checkpoint-inspection-navigator {
    display: flex;
    width: 100%;
    padding: 10px 16px;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}
.checkpoint-inspection-navigator-buttons {
    display: flex;
    align-items: center;
    gap: 12px;
}
.checkpoint-inspection-navigator-inspection-action {
    display: flex;
    align-items: center;
    gap: 12px;
}

.back-button-purple :deep(.standard-button) {
    background-color: #7c3aed !important;
    border-color: #7c3aed !important;
    color: #ffffff !important;
}
.back-button-purple :deep(.standard-button:hover) {
    background-color: #6d28d9 !important;
    border-color: #6d28d9 !important;
}
.back-button-purple :deep(.standard-button path[stroke]),
.back-button-purple :deep(.standard-button [stroke]) {
    stroke: #ffffff !important;
}
</style>